/** The record pages' shared shell plumbing (plan §1.1–§1.2): `/bookings/:id/:tab?` and
 *  `/orders/:id/:tab?` are one page shape — URL tabs that carry edit mode, a sticky spine
 *  over panels, the ledger docked beside them.
 *
 *  - `useRecordNav(basePath)` — tab hrefs keep `?edit` (and `?view`, `?facet`), the
 *    default tab has no path segment; `setParam` replaces the URL state in place.
 *  - `RecordPageTabs` — the URL tabs, with the slim progress line of a background
 *    refetch (never a spinner over content).
 *  - `useElementWidth` — the content width the spine layout and the ledger dock follow.
 *  - `missingStatus` / `retryUnlessMissing` — a 403 / 404 is an empty state, not a retry.
 */
import { Box, LinearProgress, Tab, Tabs } from '@mui/material';
import { useCallback, useLayoutEffect, useMemo, useRef, useState, type MutableRefObject } from 'react';
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router-dom';

import { ConsoleApiError } from '@/api/client';

/** The dense AppBar the AppShell scroll container sits under. */
export const APPBAR_PX = 48;
/** The scroll container's padding (AppShell `p: 3`): a sticky band (the spine, the launch
 *  band, the facet tabs) docks flush under the app bar by pinning that much higher and
 *  stretching over the padding; the ledger dock and the outline subtract it from their room. */
export const SCROLLER_PADDING_PX = 24;
/** Gap between the sticky spine and the ledger's side column. */
export const DOCK_GAP_PX = 12;
/** The query params a tab switch carries over (edit mode, the view, its tab). */
const KEPT_PARAMS = ['edit', 'view', 'facet'] as const;

export function missingStatus(e: unknown): 403 | 404 | null {
  return e instanceof ConsoleApiError && (e.status === 403 || e.status === 404) ? e.status : null;
}

export const retryUnlessMissing = (failures: number, e: unknown) => missingStatus(e) === null && failures < 3;

/** Whether the URL's tab shows the record itself while it loads: any tab but `others` (the
 *  tabs beside it this viewer may open) — the loaded page falls back to the record for a tab
 *  it does not know. */
export function loadsRecordTab(tabParam: string | undefined, others: readonly string[]): boolean {
  return tabParam === undefined || !others.includes(tabParam);
}

export function prefersReducedMotion(): boolean {
  try {
    return window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
  } catch {
    return false;
  }
}

/** Width of an element, live. 0 until measured (jsdom: always). */
export function useElementWidth<T extends HTMLElement>(): [MutableRefObject<T | null>, number] {
  const ref = useRef<T | null>(null);
  const [width, setWidth] = useState(0);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const measure = () => setWidth(el.getBoundingClientRect().width);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return [ref, width];
}

/** Kept params to set (a value) or drop (null) on the way to a tab. */
export type RecordNavParams = Partial<Record<(typeof KEPT_PARAMS)[number], string | null>>;

export interface RecordNav<Tab extends string> {
  hrefFor: (tab: Tab, params?: RecordNavParams) => string;
  goTab: (tab: Tab, params?: RecordNavParams) => void;
  setParam: (key: string, value: string | null) => void;
}

/** @param basePath `/bookings/4711` — the record's own URL (its default tab). */
export function useRecordNav<Tab extends string>(basePath: string, defaultTab: Tab): RecordNav<Tab> {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const kept = KEPT_PARAMS.map((k) => [k, params.get(k)] as const);
  const keptKey = JSON.stringify(kept);
  const hrefFor = useCallback(
    (tab: Tab, params?: RecordNavParams) => {
      const qs = new URLSearchParams();
      for (const [k, v] of JSON.parse(keptKey) as [keyof RecordNavParams, string | null][]) {
        const value = params && k in params ? params[k] : v;
        if (value) qs.set(k, value);
      }
      const search = qs.toString();
      return `${basePath}${tab === defaultTab ? '' : `/${tab}`}${search ? `?${search}` : ''}`;
    },
    [basePath, defaultTab, keptKey],
  );
  const goTab = useCallback(
    (tab: Tab, params?: RecordNavParams) => navigate(hrefFor(tab, params)),
    [navigate, hrefFor],
  );
  const setParam = useCallback(
    (key: string, value: string | null) =>
      setParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (value === null) next.delete(key);
          else next.set(key, value);
          return next;
        },
        { replace: true },
      ),
    [setParams],
  );
  return useMemo(() => ({ hrefFor, goTab, setParam }), [hrefFor, goTab, setParam]);
}

export function RecordPageTabs<Tab extends string>({
  tabs,
  active,
  hrefFor,
  fetching,
  label,
  refreshingLabel,
}: {
  tabs: { id: Tab; label: string }[];
  active: Tab;
  hrefFor: (tab: Tab) => string;
  fetching: boolean;
  /** The tab list's accessible name ("Booking sections"). */
  label: string;
  /** The refetch progress line's ("Refreshing the booking"). */
  refreshingLabel: string;
}) {
  return (
    <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2, position: 'relative' }}>
      <Tabs value={active} variant="scrollable" scrollButtons="auto" aria-label={label}>
        {tabs.map((t) => (
          <Tab key={t.id} value={t.id} label={t.label} component={RouterLink} to={hrefFor(t.id)} />
        ))}
      </Tabs>
      {fetching && (
        <LinearProgress aria-label={refreshingLabel} sx={{ position: 'absolute', left: 0, right: 0, bottom: -1, height: 2 }} />
      )}
    </Box>
  );
}
